// eslint-disable-next-line no-restricted-imports
import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  Flex,
  GridAlpha,
  PhoneInput,
  Text,
  createForm,
  useId,
  useToasts,
} from '@applyboard/crystal-ui';
import { SocialWhatsappFillIcon, SocialWhatsappOutlineIcon } from '@applyboard/ui-icons';
import { useCurrentUser, useEventTracker } from '@applyboard/ui-utils';
import { phoneInputValidatorBuilder } from '../../common/utils/phoneInputValidatorBuilder';
import { VasSpinner } from '../../components/Spinner';
import { useGetStudentDetails } from '../../data/studentDetails';
import { useSendByWhatsapp } from '../../data/testVouchers';
import normalizeVoucherType from './normalizeVoucherType';

const {
  Field: WhatsappDialogField,
  Form: WhatsappDialogForm,
  useResetForm,
} = createForm<{
  whatsappNumber: string;
  consent: boolean;
}>();

interface InternalDialogProps {
  formId: string;
  disabled?: boolean;
  isSending: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
}

function InternalDialog({ formId, disabled, open, setOpen, isSending }: InternalDialogProps) {
  const resetForm = useResetForm();

  return (
    <Dialog
      open={open}
      size="md"
      onOpenChange={open => {
        setOpen(open);
        if (!open) {
          resetForm();
        }
      }}
    >
      <Dialog.Trigger>
        <Button
          aria-label="whatsapp"
          intent="secondary"
          emphasis="transparent"
          disabled={disabled}
          onClick={() => setOpen(true)}
          leadIcon={disabled ? SocialWhatsappOutlineIcon : SocialWhatsappFillIcon}
        />
      </Dialog.Trigger>
      <Dialog.Heading>Send voucher through Whatsapp</Dialog.Heading>
      <Dialog.Content>
        <Flex direction="column" gap={4}>
          <GridAlpha columns="repeat(27, 1fr)" rowGap={6} pb={6}>
            <GridAlpha.Item columnStart={1} columnEnd={27}>
              <WhatsappDialogField
                as={PhoneInput}
                name="whatsappNumber"
                label="Whatsapp Number"
                required="Whatsapp number is required"
                validate={phoneInputValidatorBuilder()}
              />
            </GridAlpha.Item>
            <GridAlpha.Item columnStart={1} columnEnd={27}>
              <WhatsappDialogField
                as={Checkbox}
                name="consent"
                required="Consent is required"
                label={
                  <Text variant="bodyM" intent="secondary" contrast="mid">
                    <span style={{ color: '#C01C48' }}> * </span>
                    {`I've told the student to expect this message from ApplyBoard, and they've provided their consent for the message`}
                  </Text>
                }
              />
            </GridAlpha.Item>
          </GridAlpha>
        </Flex>
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton width="fill">Cancel</Dialog.CloseButton>
        <Button
          intent="primary"
          width="fill"
          type="submit"
          form={formId}
          loading={isSending}
          disabled={disabled}
        >
          Send
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}

interface SendWhatsappDialogProps {
  voucherId: string;
  voucherType: string;
  studentId: string;
  disabled?: boolean;
}

export function SendWhatsappDialog({
  voucherId,
  voucherType,
  studentId,
  disabled,
}: SendWhatsappDialogProps) {
  const formId = useId();
  const toast = useToasts();
  const [open, setOpen] = useState(false);
  const { sendVoucherByWhatsapp, isSendingVoucherByWhatsapp } = useSendByWhatsapp();
  const { studentDetails, isLoadingStudentDetails, studentDetailsError } = useGetStudentDetails(
    studentId,
    { enabled: !disabled && open },
  );
  const { trackEvent } = useEventTracker();
  const [currentUser] = useCurrentUser();

  useEffect(() => {
    if (studentDetailsError) {
      toast.warning("Unable to get the student's What'sapp number");
    }
  }, [studentDetailsError, toast]);

  if (isLoadingStudentDetails) {
    return <VasSpinner />;
  }

  return (
    <WhatsappDialogForm
      id={formId}
      defaultValues={{
        whatsappNumber: studentDetails?.contact?.phoneNumber || '',
        consent: false,
      }}
      onSubmit={values =>
        sendVoucherByWhatsapp(
          {
            voucherId: voucherId,
            whatsappNumber: values.whatsappNumber,
          },
          () => {
            toast.positive('Successfully sent the voucher by Whatsapp');
            setOpen(false);
            trackEvent({
              event: 'VAS - Vas Hub Page - Voucher Management - Voucher sent by Whatsapp',
              data: {
                rp_id: currentUser?.id,
                voucher_type: normalizeVoucherType(voucherType),
                student_id: studentId,
              },
            });
          },
          error => {
            console.error('Failed to send voucher by Whatsapp', error);
            toast.negative(new Error('Failed to send voucher by Whatsapp, please try again later'));
          },
        )
      }
    >
      <InternalDialog
        open={open}
        formId={formId}
        isSending={isSendingVoucherByWhatsapp}
        setOpen={setOpen}
        disabled={disabled}
      />
    </WhatsappDialogForm>
  );
}
